import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isAddressDefault } from "../../utils/address";
import {
  getPhilippineCityMunicipalityOptions,
  getPhilippineProvinceOptions,
} from "../../utils/philippineLocations";
import ShopSelect from "../ShopSelect";
import ConfirmedPinPicker from "../../features/delivery/components/ConfirmedPinPicker";
import { validateField } from "../../features/account/profileValidators";

const PH_REGIONS = [
  { value: "NCR", label: "NCR – National Capital Region" },
  { value: "CAR", label: "CAR – Cordillera Administrative Region" },
  { value: "I", label: "Region I – Ilocos" },
  { value: "II", label: "Region II – Cagayan Valley" },
  { value: "III", label: "Region III – Central Luzon" },
  { value: "IV-A", label: "Region IV-A – CALABARZON" },
  { value: "IV-B", label: "Region IV-B – MIMAROPA" },
  { value: "V", label: "Region V – Bicol" },
  { value: "VI", label: "Region VI – Western Visayas" },
  { value: "NIR", label: "NIR – Negros Island Region" },
  { value: "VII", label: "Region VII – Central Visayas" },
  { value: "VIII", label: "Region VIII – Eastern Visayas" },
  { value: "IX", label: "Region IX – Zamboanga Peninsula" },
  { value: "X", label: "Region X – Northern Mindanao" },
  { value: "XI", label: "Region XI – Davao Region" },
  { value: "XII", label: "Region XII – SOCCSKSARGEN" },
  { value: "XIII", label: "Region XIII – CARAGA" },
  { value: "BARMM", label: "BARMM – Bangsamoro" },
];

const PHONE_RE = /^(09\d{9}|\+639\d{9})$/;

const BLANK = {
  fullName: "",
  phone: "",
  region: "",
  province: "",
  city: "",
  barangay: "",
  addressLine1: "",
  addressLine2: "",
  landmark: "",
  postalCode: "",
  label: "",
  isDefault: false,
  latitude: null,
  longitude: null,
  pinConfirmed: false,
  locationSource: null,
  locationQuality: null,
  geocodeProvider: null,
  providerPlaceId: null,
  accuracyM: null,
};

function fromAddress(addr) {
  if (!addr) return { ...BLANK };
  return {
    fullName: addr.full_name || "",
    phone: addr.phone || "",
    region: addr.region || "",
    province: addr.province || "",
    city: addr.city || "",
    barangay: addr.barangay || "",
    addressLine1: addr.address_line1 || "",
    addressLine2: addr.address_line2 || "",
    landmark: addr.landmark || "",
    postalCode: addr.postal_code || "",
    label: addr.label || "",
    isDefault: isAddressDefault(addr),
    latitude: addr.latitude == null ? null : Number(addr.latitude),
    longitude: addr.longitude == null ? null : Number(addr.longitude),
    pinConfirmed: Boolean(addr.pin_confirmed_at) && addr.location_quality === "CONFIRMED",
    locationSource: addr.location_source || null,
    locationQuality: addr.location_quality || null,
    geocodeProvider: addr.geocode_provider || null,
    providerPlaceId: addr.provider_place_id || null,
    accuracyM: addr.accuracy_m == null ? null : Number(addr.accuracy_m),
  };
}

/* ─────────────────────────────────────────────────────────────────────────
 * Field wrapper — MUST live outside AddressForm so React does not treat it
 * as a new component type on every render (which would remount inputs and
 * steal focus on every keystroke).
 * ─────────────────────────────────────────────────────────────────────── */
function Field({ label, error, required, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-neutral-500 dark:text-neutral-400">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[11px] text-rose-500 mt-1">{error}</p>}
    </div>
  );
}

function inputBase(hasError) {
  return [
    "w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all",
    "focus:border-neutral-700 focus:ring-1 focus:ring-neutral-500 dark:focus:border-neutral-400",
    "border-neutral-200 bg-neutral-50/60 text-neutral-900 placeholder-neutral-400 focus:bg-white dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-50 dark:placeholder-neutral-500 dark:focus:bg-neutral-800",
    hasError ? "border-rose-400 dark:border-rose-500" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * AddressForm – handles create and edit.
 *
 * Props:
 *  initialData    : address object from API (null = new)
 *  onSubmit(data) : async callback — called with camelCase field object
 *  onCancel()     : back / cancel callback
 *  isDark         : boolean
 *  submitError    : string | null — API-level error shown at form bottom
 */
export default function AddressForm({ initialData, onSubmit, onCancel, submitError }) {
  const [fields, setFields] = useState(() => fromAddress(initialData));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setFields(fromAddress(initialData));
    setErrors({});
  }, [initialData]);

  const update = useCallback((key, val) => {
    setFields((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }, []);

  const selectRegion = useCallback((region) => {
    setFields((current) => ({ ...current, region, province: "", city: "" }));
    setErrors((current) => ({
      ...current,
      region: undefined,
      province: undefined,
      city: undefined,
    }));
  }, []);

  const selectProvince = useCallback((province) => {
    setFields((current) => ({ ...current, province, city: "" }));
    setErrors((current) => ({ ...current, province: undefined, city: undefined }));
  }, []);

  const provinceOptions = useMemo(
    () => getPhilippineProvinceOptions(fields.region, fields.province),
    [fields.province, fields.region]
  );
  const cityMunicipalityOptions = useMemo(
    () => getPhilippineCityMunicipalityOptions(fields.province, fields.city),
    [fields.city, fields.province]
  );

  const validate = useCallback(() => {
    const e = {};
    if (!fields.fullName.trim()) e.fullName = "Full name is required.";
    if (!fields.phone.trim()) {
      e.phone = "Phone number is required.";
    } else if (!PHONE_RE.test(fields.phone.trim())) {
      e.phone = "Enter a valid PH number (e.g. 09XXXXXXXXX).";
    }
    if (!fields.region) e.region = "Region is required.";
    if (!fields.province.trim()) e.province = "Province is required.";
    if (!fields.city.trim()) e.city = "City / Municipality is required.";
    if (!fields.addressLine1.trim()) e.addressLine1 = "Street address is required.";
    if (validateField("addressLine2", fields.addressLine2)) {
      e.addressLine2 = "Enter a valid unit / floor description of at least 6 characters, or leave it blank.";
    }
    if (!fields.postalCode.trim()) {
      e.postalCode = "ZIP code is required.";
    } else if (!/^\d{4,6}$/.test(fields.postalCode.trim())) {
      e.postalCode = "ZIP code must be 4–6 digits.";
    }
    if (!fields.pinConfirmed || !Number.isFinite(Number(fields.latitude)) || !Number.isFinite(Number(fields.longitude))) {
      e.location = "Select and confirm the exact delivery pin.";
    }
    return e;
  }, [fields]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (typeof e.nativeEvent?.stopImmediatePropagation === "function") {
      e.nativeEvent.stopImmediatePropagation();
    }

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ ...fields });
    } finally {
      setSubmitting(false);
    }
  };

  const base = inputBase(false);
  const baseErr = (hasErr) => inputBase(hasErr);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="space-y-3 py-1">

        <Field label="Full Name" error={errors.fullName} required>
          <input
            value={fields.fullName}
            onChange={(e) => update("fullName", e.target.value)}
            placeholder="First and last name"
            className={baseErr(!!errors.fullName)}
            autoComplete="name"
          />
        </Field>

        <Field label="Phone Number" error={errors.phone} required>
          <input
            value={fields.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="09XXXXXXXXX"
            inputMode="tel"
            className={baseErr(!!errors.phone)}
            autoComplete="tel"
          />
        </Field>

        <Field label="Region" error={errors.region} required>
          <ShopSelect
            value={fields.region}
            onValueChange={selectRegion}
            options={PH_REGIONS}
            placeholder="Select region..."
            invalid={Boolean(errors.region)}
            ariaLabel="Region"
            triggerClassName="rounded-xl px-4 py-3 text-sm"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Province" error={errors.province} required>
            <ShopSelect
              value={fields.province}
              onValueChange={selectProvince}
              options={provinceOptions}
              placeholder="Select province..."
              invalid={Boolean(errors.province)}
              ariaLabel="Province"
              triggerClassName="rounded-xl px-4 py-3 text-sm"
            />
          </Field>
          <Field label="City / Municipality" error={errors.city} required>
            <ShopSelect
              value={fields.city}
              onValueChange={(nextValue) => update("city", nextValue)}
              options={cityMunicipalityOptions}
              placeholder={fields.province ? "Select city / municipality..." : "Select province first..."}
              disabled={!fields.province}
              invalid={Boolean(errors.city)}
              ariaLabel="City / Municipality"
              triggerClassName="rounded-xl px-4 py-3 text-sm"
            />
          </Field>
        </div>

        <Field label="Barangay">
          <input
            value={fields.barangay}
            onChange={(e) => update("barangay", e.target.value)}
            placeholder="Barangay (optional)"
            className={base}
          />
        </Field>

        <Field label="Street / Building / House No." error={errors.addressLine1} required>
          <input
            value={fields.addressLine1}
            onChange={(e) => update("addressLine1", e.target.value)}
            placeholder="Street address, house or bldg number"
            className={baseErr(!!errors.addressLine1)}
            autoComplete="address-line1"
          />
        </Field>

        <Field label="Unit / Floor (optional)" error={errors.addressLine2}>
          <input
            value={fields.addressLine2}
            onChange={(e) => update("addressLine2", e.target.value)}
            placeholder="Unit, floor, suite, etc."
            className={baseErr(!!errors.addressLine2)}
            autoComplete="address-line2"
          />
        </Field>

        <Field label="Landmark (optional)">
          <input
            value={fields.landmark}
            onChange={(e) => update("landmark", e.target.value)}
            placeholder="Nearby landmark, gate color, or building name"
            className={base}
          />
        </Field>

        <Field label="ZIP Code" error={errors.postalCode} required>
          <input
            value={fields.postalCode}
            onChange={(e) => update("postalCode", e.target.value)}
            placeholder="e.g. 1000"
            inputMode="numeric"
            maxLength={6}
            className={baseErr(!!errors.postalCode)}
            autoComplete="postal-code"
          />
        </Field>

        <Field label="Label (optional)">
          <div className="flex gap-2 flex-wrap mb-1.5">
            {["Home", "Work", "Other"].map((lbl) => (
              <button
                key={lbl}
                type="button"
                onClick={() => update("label", fields.label === lbl ? "" : lbl)}
                className={[
                  "px-3 py-1 text-xs rounded-full border transition",
                  fields.label === lbl
                    ? "border-neutral-950 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950"
                    : "border-neutral-300 text-neutral-500 hover:border-neutral-400 dark:border-neutral-700 dark:text-neutral-400 dark:hover:border-neutral-500",
                ].join(" ")}
              >
                {lbl}
              </button>
            ))}
          </div>
          <input
            value={fields.label}
            onChange={(e) => update("label", e.target.value)}
            placeholder="or type a custom label"
            className={base}
          />
        </Field>

        <ConfirmedPinPicker
          value={fields}
          error={errors.location}
          onChange={(location) => {
            setFields((current) => ({ ...current, ...location }));
            setErrors((current) => ({ ...current, location: undefined }));
          }}
        />

        {/* Default toggle */}
        <label className="flex items-center gap-3 cursor-pointer select-none py-1">
          <input
            type="checkbox"
            checked={fields.isDefault}
            onChange={(e) => update("isDefault", e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 bg-white text-neutral-950 focus:ring-neutral-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
          />
          <span className="text-sm text-neutral-700 dark:text-neutral-300">
            Set as default address
          </span>
        </label>

        {/* API-level submit error */}
        {submitError && (
          <p className="text-xs text-rose-500 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl px-4 py-3 text-center">
            {submitError}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={[
              "flex-1 py-3 rounded-xl text-sm font-semibold border transition",
              "border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800",
            ].join(" ")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-full bg-neutral-950 py-3 text-sm font-semibold text-white transition hover:bg-neutral-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
          >
            {submitting ? "Saving…" : initialData ? "Update Address" : "Save Address"}
          </button>
        </div>
      </div>
    </form>
  );
}
